import { CloseIcon } from 'components/Drawer';
import ArrowSquareUp from 'components/icons/outline/ArrowSquareUp';
import Check from 'components/icons/outline/Check';
import ReverseLeft from 'components/icons/outline/ReverseLeft';
import ThreeStars from 'components/icons/outline/ThreeStars';
import TwoSheets from 'components/icons/outline/TwoSheets';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import copyToClipboard from 'utils/copyToClipboard';

export interface SuggestionEntryProps {
  onReplace: () => void;
  text: string;
  onRemove?: () => void;
  initialIsHidden?: boolean;
}

const SuggestionEntry = ({
  text,
  onReplace,
  initialIsHidden = false,
  onRemove,
}: SuggestionEntryProps) => {
  const [hidden, setHidden] = useState(initialIsHidden);

  if (hidden) {
    return null;
  }

  const handleAction = (action: string) => {
    switch (action) {
      case 'delete':
        onRemove?.();
        setHidden(true);
        break;
      case 'copy':
        copyToClipboard(text);
        break;
      case 'replace':
        onReplace();
        setHidden(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="p-1">
      <div className="flex flex-row gap-2 items-center">
        <div className="p-2 py-1 bg-purple-light text-primary-dark rounded-full h-8 items-center justify-center">
          <ThreeStars className="w-4 text-primary" />
        </div>
        <p className="text-xs text-primary">{text}</p>
      </div>
      <div className="flex flex-row gap-3 items-center ml-10 mt-2">
        <DeleteButton handleAction={handleAction} />
        <CopyButton handleAction={handleAction} />
        <ReplaceButton handleAction={handleAction} />
      </div>
    </div>
  );
};

type ActionButtonProps = {
  handleAction: (action: string) => void;
};

const DeleteButton = ({ handleAction }: ActionButtonProps) => {
  const [show, setShow] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    show && ReactTooltip.rebuild();
  }, [show]);

  return (
    <button
      type="button"
      onClick={() => handleAction('delete')}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      data-tip={t('reports__automatic_message_button_delete')}
    >
      <CloseIcon className="w-2.5 text-secondary-light" />
    </button>
  );
};
const CopyButton = ({ handleAction }: ActionButtonProps) => {
  const [show, setShow] = useState(false);
  const { t } = useTranslation();
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    show && ReactTooltip.rebuild();
  }, [show]);

  return (
    <button
      type="button"
      onClick={() => {
        setClicked(true);
        handleAction('copy');
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      data-tip={t('reports__automatic_message_button_copy')}
    >
      {clicked ? (
        <Check className="w-4 text-secondary-light" />
      ) : (
        <TwoSheets className="w-4 text-secondary-light" />
      )}
    </button>
  );
};

const ReplaceButton = ({ handleAction }: ActionButtonProps) => {
  const [show, setShow] = useState(false);
  const { t } = useTranslation();
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    show && ReactTooltip.rebuild();
  }, [show]);

  return (
    <button
      type="button"
      onClick={() => {
        setClicked(!clicked);
        handleAction('replace');
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      data-tip={t('reports__automatic_message_button_replace')}
    >
      {clicked ? (
        <ReverseLeft className="w-4 text-secondary-light" />
      ) : (
        <ArrowSquareUp className="w-4 text-secondary-light" />
      )}
    </button>
  );
};

export default SuggestionEntry;
